import type { Registry } from '../content/registry';
import { buildingHeight, type Building } from '../content/types';
import { customBuilding, customWing, lotToWorld, sideRot, vacantShare, wingDoorSide, wingKey, type CustomBuild } from './custom';
import type { Fields } from './fields';
import type { Lot } from './lots';
import { hashStr, Rng } from './rng';

export interface PlacedBuilding {
  key: string;
  id: string;
  def: Building;
  /** world-tile rect after rotation */
  x: number; y: number; w: number; h: number;
  /** quarter turns clockwise applied to the roof sprite (0..3) */
  rot: 0 | 1 | 2 | 3;
  height: number;
  /** collapsed to rubble (world-stream `ruins`) */
  ruined?: boolean;
  /** damage taken so far (runtime, not generated) */
  damage?: number;
  /** (owner, Sep 22) a wing of a designed house (world/custom.ts): the key of the main block, whose property it belongs to */
  main?: string;
  /** (owner, Sep 22) the doors between a house's blocks: world tiles on this block's wall where it meets a sibling (the sibling has the tile next to it) — interior.ts puts a door cell there on every floor */
  joins?: [number, number][];
  /** (owner, Sep 29: "fire escapes outside tall buildings where they fit") the iron stairs on a side or back wall: the strip of tiles
   *  outside the wall they stand on (a tile wide), which wall (0 N, 1 E, 2 S, 3 W), the wall tile each upper floor's fire door is cut
   *  into, and the tile at the foot where the ladder comes down */
  fe?: FireEscape;
}
export interface FireEscape { x: number; y: number; w: number; h: number; wall: 0 | 1 | 2 | 3; door: [number, number]; foot: [number, number] }
/** (Sep 29) the escape's landing tile for the fire door: the strip tile outside the door */
export function feLanding(fe: FireEscape): [number, number] { const [dx, dy] = fe.door; return fe.wall === 0 ? [dx, dy - 1] : fe.wall === 1 ? [dx + 1, dy] : fe.wall === 2 ? [dx, dy + 1] : [dx - 1, dy]; }
/** (Sep 29) a building with an interior and 3–10 storeys, or one whose content asks: it tries for a fire escape */
function wantsEscape(def: Building): boolean { if (def.fireEscape !== undefined) return def.fireEscape; return def.stories >= 3 && def.stories <= 10 && def.interior !== 'none'; }
/** (Sep 29) where a fire escape fits: a side wall (seen from the street) or the back, with two tiles of the lot free beyond it; the strip
 *  runs from a tile in from the front corner along the wall, up to five tiles; none where the lot leaves no room */
function fireEscapeOf(lot: Lot, x: number, y: number, w: number, h: number, fields: Fields): FireEscape | undefined {
  // the room beyond each wall, inside the lot: north, east, south, west
  const room = [y - lot.y, lot.x + lot.w - (x + w), lot.y + lot.h - (y + h), x - lot.x];
  const front = lot.facing === 0 ? 2 : lot.facing === 1 ? 3 : lot.facing === 2 ? 0 : 1; // (the wall on the road)
  const back = (front + 2) % 4, sides = [(front + 1) % 4, (front + 3) % 4];
  const order = [...(hashStr(`fe:${lot.key}`, fields.seed) % 2 ? sides.reverse() : sides), back];
  for (const wall of order as (0 | 1 | 2 | 3)[]) {
    if (room[wall] < 2) continue;
    const along = wall % 2 === 0 ? w : h; if (along < 4) continue;
    const len = Math.min(5, along - 2);
    // from the back end of a side wall (away from the street), along a back wall from its middle
    let s: FireEscape;
    if (wall === 1 || wall === 3) { // east / west wall: runs in y
      const sx = wall === 1 ? x + w : x - 1, y0 = front === 2 ? y + 1 : front === 0 ? y + h - 1 - len : y + Math.floor((h - len) / 2);
      const door: [number, number] = [wall === 1 ? x + w - 1 : x, front === 0 ? y0 + len - 1 : y0]; // (the door at the end away from the street, the ladder's foot at the street end)
      s = { x: sx, y: y0, w: 1, h: len, wall, door, foot: [sx, front === 0 ? y0 : y0 + len - 1] };
    } else { // north / south wall: runs in x
      const sy = wall === 0 ? y - 1 : y + h, x0 = front === 1 ? x + 1 : front === 3 ? x + w - 1 - len : x + Math.floor((w - len) / 2);
      const door: [number, number] = [front === 3 ? x0 + len - 1 : x0, wall === 0 ? y : y + h - 1];
      s = { x: x0, y: sy, w: len, h: 1, wall, door, foot: [front === 3 ? x0 : x0 + len - 1, sy] };
    }
    if (!groundOk(fields, s.x, s.y, s.w, s.h)) continue;
    return s;
  }
  return undefined;
}
/** (owner, Sep 22) the blocks of one house joined: where two rectangles share an edge of two tiles or more, a door in the middle of the shared stretch, one cell on each block's wall */
export function joinBlocks(blocks: PlacedBuilding[]): void {
  for (let i = 0; i < blocks.length; i++) for (let j = i + 1; j < blocks.length; j++) {
    const a = blocks[i], b = blocks[j];
    const y0 = Math.max(a.y, b.y), y1 = Math.min(a.y + a.h, b.y + b.h), x0 = Math.max(a.x, b.x), x1 = Math.min(a.x + a.w, b.x + b.w);
    if (y1 - y0 >= 2 && (a.x + a.w === b.x || b.x + b.w === a.x)) { const ym = Math.floor((y0 + y1 - 1) / 2), left = a.x < b.x ? a : b, right = left === a ? b : a; (left.joins ??= []).push([left.x + left.w - 1, ym]); (right.joins ??= []).push([right.x, ym]); }
    else if (x1 - x0 >= 2 && (a.y + a.h === b.y || b.y + b.h === a.y)) { const xm = Math.floor((x0 + x1 - 1) / 2), top = a.y < b.y ? a : b, bottom = top === a ? b : a; (top.joins ??= []).push([xm, top.y + top.h - 1]); (bottom.joins ??= []).push([xm, bottom.y]); }
  }
}

/** Deterministically choose and place a building on a lot (or nothing). */
export function placeBuilding(reg: Registry, fields: Fields, lot: Lot): PlacedBuilding | undefined {
  const rng = new Rng(hashStr(lot.key, fields.seed));
  const z = fields.zone(lot.zone);
  const custom = fields.overlay.customs.get(lot.key); // (owner, Sep 22) the plot's owner designed it (world/custom.ts) — a planned lot, or a vacant one of a generated town
  if (!rng.chance(lot.planned || lot.tight ? 1 : z.coverage) && !custom) return undefined; // (a planned lot always gets a site: the planner zoned it on purpose; a tight lot too)
  if (!custom && fields.overlay.plots.has(lot.key)) return undefined; // (owner, Sep 22) a bought plot with no design yet is bare — its owner decides what goes up (a demolished house too)
  const cands = custom ? [] : reg.forZone('building', lot.zone).filter((b) => fits(b, lot));
  if (!custom && cands.length === 0) return undefined;
  const picked = lot.planned ? fields.overlay.picks.get(lot.key) : undefined; // Q46: the planner chose
  // towns have an architectural style: matching buildings are favoured, other styles damped, unstyled neutral
  const style = fields.settlements.in(lot.x + lot.w / 2, lot.y + lot.h / 2)?.style ?? '';
  const styleMul = (b: Building) => { if (!style) return 1; const own = b.tags.filter((t) => t.startsWith('style_')); if (!own.length) return 1; return own.includes(style) ? 3 : 0.4; };
  // (§57) a tight lot wants filling: the widest and deepest that fit weigh most, and anything under three quarters of the
  // frontage is only taken when nothing wider fits
  const fill = (b: Building) => (lot.tight ? (b.footprint[0] / lot.front) ** 2 * (b.footprint[1] / lot.depth) : 1);
  const pool = lot.tight && cands.some((b) => b.footprint[0] >= lot.front * 0.75) ? cands.filter((b) => b.footprint[0] >= lot.front * 0.75) : cands;
  const def = custom ? customBuilding(lot.key, custom) : (picked && cands.find((b) => b.id === picked)) ?? rng.weighted(pool, (b) => (b.zones[lot.zone] ?? 0) * (b.rarity ?? 1) * styleMul(b) * fill(b));
  if (!def) return undefined;

  const [fw, fd] = def.footprint;
  const setback = lot.tight ? 0 : def.setback ?? 0;
  const slack = lot.front - fw;
  const off = custom ? Math.max(0, Math.min(Math.max(0, slack), custom.off)) : lot.tight ? 0 : slack > 0 ? rng.int(0, slack) : 0; // (tight: flush with the lot's start, so the row packs; a design: where its owner put it)
  let x: number, y: number, w: number, h: number;
  let rot: 0 | 1 | 2 | 3;
  switch (lot.facing) {
    case 0: x = lot.x + off; y = lot.y + lot.h - setback - fd; w = fw; h = fd; rot = 0; break; // road south
    case 2: x = lot.x + off; y = lot.y + setback; w = fw; h = fd; rot = 2; break; // road north
    case 1: x = lot.x + setback; y = lot.y + off; w = fd; h = fw; rot = 1; break; // road west
    default: x = lot.x + lot.w - setback - fd; y = lot.y + off; w = fd; h = fw; rot = 3; break; // road east
  }
  if (!(def.rotatable ?? true) && rot !== 0) rot = 0;
  if (!groundOk(fields, x, y, w, h)) return undefined;
  const fe = !custom && wantsEscape(def) ? fireEscapeOf(lot, x, y, w, h, fields) : undefined;
  return { key: lot.key, id: def.id, def, x, y, w, h, rot, height: buildingHeight(def), ...(fe ? { fe } : {}) };
}

export function fits(b: Building, lot: Lot): boolean {
  const [fw, fd] = b.footprint;
  if (fw > lot.front) return false;
  if (fd + (b.setback ?? 0) > lot.depth) return false;
  if (b.minLotDepth !== undefined && lot.depth < b.minLotDepth) return false;
  return true;
}

/** (owner, Sep 22) the wings of a design on its lot: annex buildings keyed `<lot>#<n>`, each turned so its door is in a free wall (`wingDoorSide`), owned with the main block (`main`) */
export function placeWings(reg: Registry, fields: Fields, lot: Lot, spec: CustomBuild, main: PlacedBuilding): PlacedBuilding[] {
  const out: PlacedBuilding[] = [];
  (spec.wings ?? []).forEach((r, i) => {
    const wr = lotToWorld(lot, r), side = wingDoorSide(spec, i) ?? 'front', rot = sideRot(lot.facing, side);
    const def = customWing(lot.key, spec, i, rot % 2 ? wr.h : wr.w, rot % 2 ? wr.w : wr.h); reg.setCustom(def); // (the sprite's w × h follows the turn: the world rect is what the design drew)
    if (!groundOk(fields, wr.x, wr.y, wr.w, wr.h)) return;
    out.push({ key: wingKey(lot.key, i), id: def.id, def, x: wr.x, y: wr.y, w: wr.w, h: wr.h, rot, height: buildingHeight(def), main: `${main.x},${main.y}` });
  });
  return out;
}
/** (owner, Sep 22) a lot the generator left bare, on buildable ground: a plot for sale — every empty lot of a town, a hashed share of the country's (world/custom.ts `vacantShare`) */
export function vacantLot(fields: Fields, lot: Lot): boolean {
  if (!groundOk(fields, lot.x, lot.y, lot.w, lot.h)) return false;
  const share = vacantShare(lot.zone); return share >= 1 || (hashStr(`vacant:${lot.key}`, fields.seed) % 1000) / 1000 < share;
}
/** Every tile of the footprint must be buildable (sample corners, centre and a coarse grid). */
export function groundOk(fields: Fields, x: number, y: number, w: number, h: number): boolean {
  const step = 4;
  for (let ty = y; ty <= y + h - 1; ty += Math.min(step, h)) {
    for (let tx = x; tx <= x + w - 1; tx += Math.min(step, w)) {
      if (!fields.terrainAt(tx, ty).buildable) return false;
    }
  }
  if (!fields.terrainAt(x + w - 1, y + h - 1).buildable) return false;
  if (!fields.terrainAt(x + w - 1, y).buildable) return false;
  if (!fields.terrainAt(x, y + h - 1).buildable) return false;
  return true;
}
